import { API_BASE_URL } from "../config/api";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import PhoneActions from "../components/PhoneActions";
import SerialProductDetails from "../components/SerialProductDetails";
import {
  downloadProductLabelsPdf,
  downloadReportPdf,
  openProductLabelsPdf,
  openReportPdf
} from "../utils/reportApi";

const formatDisplayDate = (value) => {
  if (!value) return "-";

  const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
  if (ddmmyyyy.test(String(value))) return value;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString("en-GB");
};

const normalizeApprovalStatus = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "approved") return "Approved";
  if (normalized === "edit access granted") return "Edit Access Granted";
  if (normalized === "re-approved/locked" || normalized === "re-approved") return "Re-Approved/Locked";
  if (normalized === "changes requested" || normalized === "rejected" || normalized === "not approved") {
    return "Changes Requested";
  }
  return "Approval Pending";
};

const BREAKER_PRODUCT_LABELS = {
  acb: "ACB (Air circuit breaker)",
  mccb: "MCCB (Molded case circuit breaker)",
  vcb: "VCB (Vacuum circuit breaker)"
};

const getBreakerDisplayName = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const lowerValue = normalized.toLowerCase();
  if (BREAKER_PRODUCT_LABELS[lowerValue]) {
    return BREAKER_PRODUCT_LABELS[lowerValue];
  }

  const compactValue = lowerValue.split("(")[0].trim();
  return BREAKER_PRODUCT_LABELS[compactValue] || normalized;
};

const extractReplacementComponents = (products = []) =>
  (Array.isArray(products) ? products : [])
    .flatMap((product) =>
      Array.isArray(product?.compReplace) ? product.compReplace : []
    )
    .map((component) => String(component || "").trim())
    .filter(Boolean);

export default function ReportView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const role = localStorage.getItem("role") || "";
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [requestingChanges, setRequestingChanges] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reviewRemarks, setReviewRemarks] = useState("");
  const [showSendBackBox, setShowSendBackBox] = useState(false);
  const [showApproveBox, setShowApproveBox] = useState(false);
  const [approvalFinalStatus, setApprovalFinalStatus] = useState("completed");
  const [expandedProductIndex, setExpandedProductIndex] = useState(null);
  const [dynamicTeams, setDynamicTeams] = useState([]);
  const [customTeamsOrder, setCustomTeamsOrder] = useState([]);
  const [arrangeBy, setArrangeBy] = useState("default");
  const [updatingOrder, setUpdatingOrder] = useState(false);
  const [selectedTeamFilter, setSelectedTeamFilter] = useState("all");
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showPdfDropdown, setShowPdfDropdown] = useState(false);
  const actionsDropdownRef = useRef(null);
  const pdfDropdownRef = useRef(null);
  const isEngineerView = location.pathname.startsWith("/my-reports");
  const isAccountsView = location.pathname.startsWith("/accounts");
  const canReviewReport = role === "admin";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target)) {
        setShowActionsDropdown(false);
      }
      if (pdfDropdownRef.current && !pdfDropdownRef.current.contains(event.target)) {
        setShowPdfDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchReportAndSettings = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        const [reportRes, settingsRes] = await Promise.all([
          axios.get(API_BASE_URL + `/api/visits/${id}`, {
            headers,
            params: isEngineerView
              ? {
                  engineer:
                    localStorage.getItem("engineerId") ||
                    localStorage.getItem("engineerEmail") ||
                    localStorage.getItem("engineerName") ||
                    ""
                }
              : undefined
          }),
          role === "admin"
            ? axios.get(API_BASE_URL + "/api/admin-settings/system-settings", { headers }).catch(() => ({ data: null }))
            : Promise.resolve({ data: null })
        ]);

        const responseData = reportRes.data;
        setReport(responseData);
        setReviewRemarks(responseData?.reviewRemarks || "");
        let finalStatusLower = String(responseData?.finalStatus || "completed").toLowerCase();
        if (finalStatusLower === "customer waiting for order") {
          finalStatusLower = "need to provide quote";
        }
        const validStatuses = ["completed", "pending", "re-visit", "need to provide quote", "other"];
        setApprovalFinalStatus(validStatuses.includes(finalStatusLower) ? finalStatusLower : "completed");
        setExpandedProductIndex(null);

        if (settingsRes?.data) {
          const teamsList = settingsRes.data.teams && Array.isArray(settingsRes.data.teams) ? settingsRes.data.teams : [
            { id: "team_1", name: "Team 1" },
            { id: "team_2", name: "Team 2" },
            { id: "team_3", name: "Team 3" }
          ];
          setDynamicTeams(teamsList);
          setCustomTeamsOrder(teamsList.map(t => t.id));
        } else {
          const fallbackTeams = [
            { id: "team_1", name: "Team 1" },
            { id: "team_2", name: "Team 2" },
            { id: "team_3", name: "Team 3" }
          ];
          setDynamicTeams(fallbackTeams);
          setCustomTeamsOrder(fallbackTeams.map(t => t.id));
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || error.message || "Unknown error";
        alert(`Failed to load report: ${errorMsg}`);
      } finally {
        setLoading(false);
      }
    };

    fetchReportAndSettings();
  }, [id, isEngineerView, role]);

  const updateProductsOrder = async (sortedProducts) => {
    try {
      setUpdatingOrder(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
      const payload = {
        ...report,
        products: sortedProducts
      };
      const response = await axios.put(API_BASE_URL + `/api/visits/${id}`, payload, { headers });
      setReport(response.data);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || error.message || "Failed to update order");
    } finally {
      setUpdatingOrder(false);
    }
  };

  const handleArrangeChange = async (type) => {
    if (!report || !Array.isArray(report.products)) return;
    setArrangeBy(type);
    let sorted = [...report.products];

    const getTeamIndex = (prod) => {
      const teamValue = prod.sourceTeam || prod.team || "";
      if (!teamValue) return 9999;
      const idx = customTeamsOrder.indexOf(teamValue);
      if (idx !== -1) return idx;
      if (teamValue === "team_1") return 0;
      if (teamValue === "team_2") return 1;
      if (teamValue === "team_3") return 2;
      return 9999;
    };

    if (type === "team") {
      sorted.sort((a, b) => getTeamIndex(a) - getTeamIndex(b));
    } else if (type === "panel") {
      sorted.sort((a, b) => {
        const areaA = String(a.panelArea || "").trim().toLowerCase();
        const areaB = String(b.panelArea || "").trim().toLowerCase();
        if (!areaA) return 1;
        if (!areaB) return -1;
        return areaA.localeCompare(areaB);
      });
    } else if (type === "team_panel") {
      sorted.sort((a, b) => {
        const teamA = getTeamIndex(a);
        const teamB = getTeamIndex(b);
        if (teamA !== teamB) return teamA - teamB;

        const areaA = String(a.panelArea || "").trim().toLowerCase();
        const areaB = String(b.panelArea || "").trim().toLowerCase();
        if (!areaA) return 1;
        if (!areaB) return -1;
        return areaA.localeCompare(areaB);
      });
    }

    await updateProductsOrder(sorted);
  };

  const moveProduct = async (index, direction) => {
    if (!report || !Array.isArray(report.products)) return;
    const updated = [...report.products];
    if (direction === "up" && index > 0) {
      const temp = updated[index];
      updated[index] = updated[index - 1];
      updated[index - 1] = temp;
    } else if (direction === "down" && index < updated.length - 1) {
      const temp = updated[index];
      updated[index] = updated[index + 1];
      updated[index + 1] = temp;
    }
    setArrangeBy("custom");
    await updateProductsOrder(updated);
  };

  const moveTeamOrder = async (index, direction) => {
    const updated = [...customTeamsOrder];
    if (direction === "up" && index > 0) {
      const temp = updated[index];
      updated[index] = updated[index - 1];
      updated[index - 1] = temp;
    } else if (direction === "down" && index < updated.length - 1) {
      const temp = updated[index];
      updated[index] = updated[index + 1];
      updated[index + 1] = temp;
    }
    setCustomTeamsOrder(updated);

    if (arrangeBy === "team" || arrangeBy === "team_panel") {
      if (!report || !Array.isArray(report.products)) return;
      let sorted = [...report.products];
      const getTeamIndex = (prod) => {
        const teamValue = prod.sourceTeam || prod.team || "";
        if (!teamValue) return 9999;
        const idx = updated.indexOf(teamValue);
        if (idx !== -1) return idx;
        if (teamValue === "team_1") return 0;
        if (teamValue === "team_2") return 1;
        if (teamValue === "team_3") return 2;
        return 9999;
      };
      if (arrangeBy === "team") {
        sorted.sort((a, b) => getTeamIndex(a) - getTeamIndex(b));
      } else {
        sorted.sort((a, b) => {
          const teamA = getTeamIndex(a);
          const teamB = getTeamIndex(b);
          if (teamA !== teamB) return teamA - teamB;
          const areaA = String(a.panelArea || "").trim().toLowerCase();
          const areaB = String(b.panelArea || "").trim().toLowerCase();
          if (!areaA) return 1;
          if (!areaB) return -1;
          return areaA.localeCompare(areaB);
        });
      }
      await updateProductsOrder(sorted);
    }
  };

  const goBackPath = isEngineerView ? "/my-reports" : isAccountsView ? "/accounts/reports" : "/complaints";
  const editPath = isEngineerView ? `/create-report/${id}` : `/reports/${id}/edit`;
  const content = loading ? (
    <div className="p-6">Loading report...</div>
  ) : !report ? (
    <div className="p-6">Report not found.</div>
  ) : (
    (() => {
      const approvalStatus = normalizeApprovalStatus(report.approvalStatus);
      const isApproved = approvalStatus === "Approved" || approvalStatus === "Re-Approved/Locked";
      const isChangesRequested = approvalStatus === "Changes Requested";
      const isAbbWarranty = (() => {
        const cId = String(report.complaintRefId || report.complaintId || report.reportId || "").toUpperCase();
        const pType = String(report.productType || "").toLowerCase();
        const rType = String(report.reportType || "").toLowerCase();
        const customer = String(report.customerName || report.company || "").toLowerCase();
        const issue = String(report.complaint || report.issue || report.siteAdvice || "").toLowerCase();

        return (
          cId.includes("/ABB-") ||
          cId.includes("ABB-") ||
          pType.includes("abb") ||
          pType.includes("warranty") ||
          rType.includes("abb") ||
          customer.includes("abb india") ||
          issue.includes("abb warranty") ||
          Boolean(report.isAbbWarranty)
        );
      })();
      const servicedProducts = Array.isArray(report.products) ? report.products : [];
      const getProductTeamId = (p) => p.sourceTeam || p.team || "";
      const presentTeamIds = Array.from(new Set(
        servicedProducts.map(p => getProductTeamId(p)).filter(Boolean)
      ));
      const getTeamDisplayName = (teamId) => {
        if (!teamId) return "Unassigned";
        const matchedTeam = dynamicTeams.find(t => t.id === teamId || t.name === teamId);
        return matchedTeam ? matchedTeam.name : (teamId === "team_1" ? "Team 1" : teamId === "team_2" ? "Team 2" : teamId === "team_3" ? "Team 3" : teamId);
      };
      const hasUnassignedProducts = servicedProducts.some(p => !getProductTeamId(p));
      const filteredProducts = servicedProducts.filter(p => {
        if (selectedTeamFilter === "all") return true;
        if (selectedTeamFilter === "unassigned") return !getProductTeamId(p);
        return getProductTeamId(p) === selectedTeamFilter;
      });
      const getProductTitle = (product, index) =>
        (product?.category === "breaker"
          ? getBreakerDisplayName(product?.productName || product?.product)
          : product?.product || product?.productName) || product?.feeder || `Product ${index + 1}`;
      const getProductMeta = (product) => [
        product?.serialNo && product.serialNo !== "-" ? `Serial: ${product.serialNo}` : "",
        product?.rating && product.rating !== "-" ? `Rating: ${product.rating}` : "",
        product?.feeder && product.feeder !== "-" ? `Feeder: ${product.feeder}` : "",
        product?.serviceStatus && product.serviceStatus !== "-" ? product.serviceStatus : product?.status && product.status !== "-" ? product.status : ""
      ].filter(Boolean);
      const getProductDotColor = (product) => {
        const hasToBeReplaced = (
          (Array.isArray(product?.componentsToBeReplaced) && product.componentsToBeReplaced.some(c => c.name)) ||
          (Array.isArray(product?.compReplace) && product.compReplace.some(Boolean))
        );
        const hasBeenReplaced = (
          (Array.isArray(product?.componentsReplaced) && product.componentsReplaced.some(c => c.name)) ||
          (Array.isArray(product?.newComp) && product.newComp.some(Boolean))
        );

        if (!hasToBeReplaced) {
          return "bg-emerald-500";
        }
        if (hasToBeReplaced && hasBeenReplaced) {
          return "bg-amber-500";
        }
        return "bg-rose-500";
      };
      const details = [
        { label: "Report ID", value: report.reportId || "-" },
        { label: "Complaint ID", value: report.complaintRefId || report.complaintId || "-" },
        { label: "Customer", value: report.customerName || report.company || "-" },
        { label: "Company", value: report.company || "-" },
        { label: "Engineer", value: report.engineer || "-" },
        { label: "Visit Date", value: formatDisplayDate(report.visitDate || report.date) },
        { label: "Contact Name", value: report.contactName || "-" },
        { label: "Phone", value: <PhoneActions phone={report.phone || ""} /> },
        { label: "Final Status", value: report.finalStatus || "-" }
      ];

      const generateQuotation = () => {
        const componentsToReplace = extractReplacementComponents(servicedProducts);
        if (componentsToReplace.length === 0) {
          alert("No components to be replaced were found in this report.");
          return;
        }

        navigate("/admin/quotations", {
          state: {
            quotationPrefill: {
              reportId: report.reportId || "",
              reportDate: report.visitDate || report.date || "",
              customerName: report.customerName || report.company || "",
              company: report.company || "",
              contactPerson: report.contactName || "",
              phone: report.phone || "",
              email: report.email || "",
              siteAddress: report.address || "",
              city: report.city || "",
              siteDetails: {
                address: report.address || "",
                city: report.city || ""
              },
              complaint: report.complaint || "",
              componentsToReplace
            },
            fromPath: location.pathname
          }
        });
      };

      const approveReport = async () => {
        if (!approvalFinalStatus) {
          alert("Choose a final complaint status before approving the report.");
          return;
        }

        try {
          setApproving(true);
          const approvedBy = localStorage.getItem("adminName") || "Admin";
          let response;

          try {
            response = await axios.put(API_BASE_URL + `/api/visits/${id}/approve`, {
              approvedBy,
              finalStatus: approvalFinalStatus
            });
          } catch (endpointError) {
            response = await axios.put(API_BASE_URL + `/api/visits/${id}`, {
              ...report,
              approvalStatus: "Approved",
              finalStatus: approvalFinalStatus,
              reviewRemarks: "",
              approvedBy,
              approvedAt: new Date().toISOString()
            });
          }

          setReport(response.data);
          setReviewRemarks("");
          setShowApproveBox(false);
          setShowSendBackBox(false);
          alert("Report approved successfully.");
        } catch (error) {
          alert(error.response?.data?.message || "Failed to approve report");
        } finally {
          setApproving(false);
        }
      };

      const sendBackToEngineer = async () => {
        const remarks = String(reviewRemarks || "").trim();
        if (!remarks) {
          alert("Enter remarks before sending the report back to the engineer.");
          return;
        }

        try {
          setRequestingChanges(true);
          let response;

          try {
            response = await axios.put(API_BASE_URL + `/api/visits/${id}/request-changes`, {
              reviewRemarks: remarks
            });
          } catch (endpointError) {
            response = await axios.put(API_BASE_URL + `/api/visits/${id}`, {
              ...report,
              approvalStatus: "Changes Requested",
              reviewRemarks: remarks,
              approvedAt: null,
              approvedBy: ""
            });
          }

          setReport(response.data);
          setReviewRemarks(response.data?.reviewRemarks || remarks);
          setShowSendBackBox(false);
          alert("Report sent back to the engineer successfully.");
        } catch (error) {
          alert(error.response?.data?.message || "Failed to send report back to engineer");
        } finally {
          setRequestingChanges(false);
        }
      };

      const handleUpdateEditAccess = async (action) => {
        try {
          let response;
          const token = localStorage.getItem("token");
          const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
          if (action === "grant") {
            response = await axios.put(API_BASE_URL + `/api/visits/${id}/grant-edit`, {}, { headers });
            alert("Edit access granted successfully.");
          } else {
            response = await axios.put(API_BASE_URL + `/api/visits/${id}/revoke-edit`, {
              action: action
            }, { headers });
            alert(action === "re-approve" ? "Report re-approved successfully." : "Edit access revoked successfully.");
          }
          setReport(response.data);
        } catch (error) {
          alert(error.response?.data?.message || `Failed to ${action} edit access`);
        }
      };

      const startRevisitComplaint = async () => {
        const complaintIdentifier = report.complaintRefId || report.complaintId;
        if (!complaintIdentifier) {
          alert("Complaint details are not available for this report.");
          return;
        }

        try {
          const complaintRes = await axios.get(API_BASE_URL + `/api/complaints/${encodeURIComponent(complaintIdentifier)}`);
          const complaint = complaintRes.data;
          const revisitRes = await axios.get(API_BASE_URL + `/api/complaints/${complaint._id || complaint.complaintId}/revisit-id`);

          navigate("/create-complaint", {
            state: {
              prefill: {
                ...complaint,
                complaintId: revisitRes.data?.complaintId || `${complaint.complaintId}-1`,
                revisitOf: revisitRes.data?.revisitOf || complaint._id,
                originalComplaintId: revisitRes.data?.originalComplaintId || complaint.complaintId,
                engineerAssigned: "",
                siteAdvice: ""
              }
            }
          });
        } catch (error) {
          alert(error.response?.data?.message || "Failed to start revisit complaint");
        }
      };

      const deleteReport = async () => {
        const confirmed = window.confirm(
          `Are you sure you want to delete report ${report.reportId || ""}?`
        );
        if (!confirmed) return;

        try {
          setDeleting(true);
          const token = localStorage.getItem("token");
          const targetId = report?._id || id;
          const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

          try {
            await axios.delete(API_BASE_URL + `/api/reports/${targetId}`, {
              headers
            });
          } catch (reportDeleteError) {
            await axios.delete(API_BASE_URL + `/api/visits/${targetId}`, {
              headers
            });
          }

          alert("Report deleted successfully.");
          navigate(goBackPath);
        } catch (error) {
          alert(
            error.response?.data?.message ||
            `Failed to delete report${error.response?.status ? ` (${error.response.status})` : ""}`
          );
        } finally {
          setDeleting(false);
        }
      };

      return (
    <div className="min-h-screen bg-slate-50/70 p-3 sm:p-4 md:p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Top Header & Actions Toolbar */}
        <div className="bg-white border border-slate-200/80 rounded-2xl p-4 sm:p-5 shadow-2xs flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                SERVICE INSPECTION REPORT
              </span>
              <span className="text-xs font-mono font-bold text-slate-500">
                #{report.reportId || report.visitId || id}
              </span>
            </div>
            <h1 className="text-xl md:text-2xl font-black tracking-tight text-slate-900 flex items-center gap-2">
              <span className="material-symbols-outlined text-blue-600 text-2xl">description</span>
              Report Details
            </h1>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold border ${
                approvalStatus === "Approved"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : approvalStatus === "Re-Approved/Locked"
                  ? "bg-teal-50 text-teal-700 border-teal-200"
                  : approvalStatus === "Edit Access Granted"
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : isChangesRequested
                  ? "bg-rose-50 text-rose-700 border-rose-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }`}>
                <span className={`h-2 w-2 rounded-full ${
                  approvalStatus === "Approved"
                    ? "bg-emerald-500"
                    : approvalStatus === "Re-Approved/Locked"
                    ? "bg-teal-500"
                    : approvalStatus === "Edit Access Granted"
                    ? "bg-blue-500"
                    : isChangesRequested
                    ? "bg-rose-500"
                    : "bg-amber-500"
                }`} />
                {approvalStatus}
              </span>
              {report.finalStatus && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                  Status: {report.finalStatus}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap items-center">
            {/* Separate Approve Button */}
            {canReviewReport && !isEngineerView && !report.editAccessGranted && (
              <button
                onClick={() => setShowApproveBox(true)}
                disabled={isApproved || approving}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-bold text-white shadow-2xs transition-all ${
                  isApproved || approving
                    ? "bg-emerald-400 cursor-not-allowed opacity-80"
                    : "bg-emerald-600 hover:bg-emerald-700 active:scale-95"
                }`}
              >
                <span className="material-symbols-outlined text-base">check_circle</span>
                {isApproved ? "Approved" : approving ? "Approving..." : "Approve"}
              </button>
            )}

            {/* Separate PDF Dropdown */}
            <div className="relative inline-block text-left" ref={pdfDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setShowPdfDropdown((prev) => !prev);
                  setShowActionsDropdown(false);
                }}
                className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:scale-95 transition-all text-white px-3 py-1.5 rounded-xl text-xs sm:text-sm font-bold shadow-2xs"
              >
                <span className="material-symbols-outlined text-base">picture_as_pdf</span>
                <span>PDF</span>
                <span className="material-symbols-outlined text-sm">arrow_drop_down</span>
              </button>

              {showPdfDropdown && (
                <div className="absolute right-0 mt-1.5 w-44 rounded-xl bg-white shadow-lg border border-slate-200 py-1.5 z-30 focus:outline-none">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPdfDropdown(false);
                      openReportPdf(id);
                    }}
                    className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition"
                  >
                    <span className="material-symbols-outlined text-base text-blue-600">open_in_new</span>
                    Open PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowPdfDropdown(false);
                      downloadReportPdf(id);
                    }}
                    className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2 transition"
                  >
                    <span className="material-symbols-outlined text-base text-emerald-600">download</span>
                    Download PDF
                  </button>
                </div>
              )}
            </div>

            {/* Actions Dropdown */}
            <div className="relative inline-block text-left" ref={actionsDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setShowActionsDropdown((prev) => !prev);
                  setShowPdfDropdown(false);
                }}
                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-900 active:scale-95 transition-all text-white px-3 py-1.5 rounded-xl text-xs sm:text-sm font-bold shadow-2xs"
              >
                <span className="material-symbols-outlined text-base">more_vert</span>
                <span>Actions</span>
                <span className="material-symbols-outlined text-sm">arrow_drop_down</span>
              </button>

              {showActionsDropdown && (
                <div className="absolute right-0 mt-1.5 w-52 rounded-xl bg-white shadow-lg border border-slate-200 py-1.5 z-30 focus:outline-none divide-y divide-slate-100">
                  <div className="py-1">
                    {(canReviewReport || (isEngineerView && !isApproved && !report.mergeLocked)) && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false);
                          navigate(editPath);
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-amber-50 hover:text-amber-800 flex items-center gap-2 transition"
                      >
                        <span className="material-symbols-outlined text-base text-amber-500">edit</span>
                        Edit
                      </button>
                    )}

                    {canReviewReport && !isEngineerView && !isAccountsView && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false);
                          generateQuotation();
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-orange-50 hover:text-orange-800 flex items-center gap-2 transition"
                      >
                        <span className="material-symbols-outlined text-base text-orange-600">request_quote</span>
                        Generate Quotation
                      </button>
                    )}

                    {canReviewReport && !isEngineerView && !report.editAccessGranted && !isApproved && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false);
                          setShowSendBackBox(true);
                        }}
                        disabled={requestingChanges}
                        className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-rose-50 hover:text-rose-700 flex items-center gap-2 transition"
                      >
                        <span className="material-symbols-outlined text-base text-rose-600">undo</span>
                        Send Back
                      </button>
                    )}

                    {canReviewReport && !isEngineerView && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false);
                          startRevisitComplaint();
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-purple-50 hover:text-purple-700 flex items-center gap-2 transition"
                      >
                        <span className="material-symbols-outlined text-base text-purple-600">replay</span>
                        Revisit
                      </button>
                    )}

                    {(role === "admin" || role === "manager") && !isEngineerView && isApproved && !report.editAccessGranted && (
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false);
                          handleUpdateEditAccess("grant");
                        }}
                        className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition"
                      >
                        <span className="material-symbols-outlined text-base text-blue-600">lock_open</span>
                        Grant Edit Access
                      </button>
                    )}

                    {(role === "admin" || role === "manager") && !isEngineerView && report.editAccessGranted && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setShowActionsDropdown(false);
                            handleUpdateEditAccess("re-approve");
                          }}
                          className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2 transition"
                        >
                          <span className="material-symbols-outlined text-base text-emerald-600">lock</span>
                          Re-Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowActionsDropdown(false);
                            handleUpdateEditAccess("revoke");
                          }}
                          className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-slate-700 hover:bg-rose-50 hover:text-rose-700 flex items-center gap-2 transition"
                        >
                          <span className="material-symbols-outlined text-base text-rose-600">cancel</span>
                          Revoke Access
                        </button>
                      </>
                    )}
                  </div>

                  {canReviewReport && !isEngineerView && (
                    <div className="py-1">
                      <button
                        type="button"
                        onClick={() => {
                          setShowActionsDropdown(false);
                          deleteReport();
                        }}
                        disabled={deleting}
                        className="w-full text-left px-3.5 py-2 text-xs sm:text-sm font-semibold text-rose-600 hover:bg-rose-50 flex items-center gap-2 transition"
                      >
                        <span className="material-symbols-outlined text-base text-rose-600">delete</span>
                        {deleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Back Button */}
            <button
              onClick={() => navigate(goBackPath)}
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 active:scale-95 transition-all text-slate-700 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-bold border border-slate-300 shadow-2xs"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back
            </button>
          </div>
        </div>

        {/* Overview & Metadata Card */}
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs p-4 sm:p-5 space-y-4">
          {isChangesRequested && report.reviewRemarks && (
            <div className="p-3.5 rounded-xl border border-rose-200 bg-rose-50 text-xs sm:text-sm font-semibold text-rose-700 flex items-center gap-2">
              <span className="material-symbols-outlined text-base text-rose-600 shrink-0">error</span>
              <span><strong>Sent back to engineer with remarks:</strong> {report.reviewRemarks}</span>
            </div>
          )}

          {report.mergeLocked && report.reportType === "engineer" && (
            <div className="p-3.5 rounded-xl border border-sky-200 bg-sky-50 text-xs sm:text-sm font-semibold text-sky-700 flex items-center gap-2">
              <span className="material-symbols-outlined text-base text-sky-600 shrink-0">lock</span>
              <span>This engineer report is locked because a final merged report has already been created.</span>
            </div>
          )}

          {canReviewReport && !isEngineerView && showSendBackBox && (
            <div className="p-4 rounded-xl border border-rose-200 bg-rose-50/60 space-y-3">
              <label className="block text-xs sm:text-sm font-bold text-rose-900">
                Review Remarks
              </label>
              <textarea
                value={reviewRemarks}
                onChange={(event) => setReviewRemarks(event.target.value)}
                placeholder="Enter remarks for the engineer before sending the report back."
                className="w-full rounded-xl border border-rose-200 bg-white p-2.5 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-rose-500 shadow-2xs"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={sendBackToEngineer}
                  disabled={requestingChanges}
                  className="rounded-xl bg-rose-600 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-rose-300 shadow-2xs"
                >
                  {requestingChanges ? "Sending..." : "Send To Engineer"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSendBackBox(false)}
                  disabled={requestingChanges}
                  className="rounded-xl bg-slate-200 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-slate-700 hover:bg-slate-300 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {canReviewReport && !isEngineerView && showApproveBox && !isApproved && (
            <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/60 space-y-3">
              <label className="block text-xs sm:text-sm font-bold text-emerald-900">
                Choose final complaint status after approval
              </label>
              <select
                value={approvalFinalStatus}
                onChange={(event) => setApprovalFinalStatus(event.target.value)}
                className="w-full rounded-xl border border-emerald-200 p-2.5 text-xs sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 shadow-2xs font-medium"
              >
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="re-visit">Re-Visit</option>
                <option value="need to provide quote">Need to provide quote</option>
                <option value="other">Other</option>
              </select>
              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={approveReport}
                  disabled={approving}
                  className="rounded-xl bg-emerald-600 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300 shadow-2xs"
                >
                  {approving ? "Approving..." : "Confirm Approval"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowApproveBox(false)}
                  disabled={approving}
                  className="rounded-xl bg-slate-200 px-3.5 py-1.5 text-xs sm:text-sm font-bold text-slate-700 hover:bg-slate-300 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Key Details Summary */}
          <div>
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-sm text-slate-500">info</span>
              General Details &amp; Summary
            </h3>
            <div className="bg-slate-50/70 border border-slate-200/70 rounded-xl p-3 sm:p-3.5 text-xs divide-y divide-slate-200/60 space-y-2.5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-2.5">
                {details.map((item) => (
                  <div key={item.label} className="min-w-0">
                    <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">{item.label}</span>
                    <div className="font-bold text-slate-800 break-words text-xs mt-0.5 leading-snug">
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {(report.address || report.complaint) && (
                <div className="pt-2.5 space-y-2 text-xs">
                  {report.address && (
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                      <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider sm:w-20 shrink-0">Address:</span>
                      <span className="text-slate-700 leading-relaxed break-words">{report.address}</span>
                    </div>
                  )}
                  {report.complaint && (
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                      <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider sm:w-20 shrink-0">Complaint:</span>
                      <span className="text-slate-700 leading-relaxed whitespace-pre-wrap break-words">{report.complaint}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Products Serviced Card */}
        <div className="bg-white border border-slate-200/80 rounded-2xl shadow-2xs p-4 sm:p-5 space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-3">
            <div>
              <h2 className="text-base sm:text-lg font-black text-slate-900 flex items-center gap-2">
                <span className="material-symbols-outlined text-blue-600">precision_manufacturing</span>
                Products Serviced
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                {servicedProducts.length > 0
                  ? `${servicedProducts.length} product${servicedProducts.length === 1 ? "" : "s"} recorded for this report.`
                  : "No product details were recorded for this report."}
              </p>
            </div>

            {!isAbbWarranty && servicedProducts.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openProductLabelsPdf(report?._id || id)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-slate-800 px-3 py-1.5 text-xs font-bold text-white hover:bg-slate-900 transition shadow-2xs"
                >
                  <span className="material-symbols-outlined text-sm">label</span>
                  Sticker PDF
                </button>
                <button
                  type="button"
                  onClick={() => downloadProductLabelsPdf(report?._id || id)}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 transition shadow-2xs"
                >
                  <span className="material-symbols-outlined text-sm">download</span>
                  Download Stickers
                </button>
              </div>
            )}
          </div>

          {!isAbbWarranty && canReviewReport && servicedProducts.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-3 sm:p-4 shadow-2xs space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs sm:text-sm font-bold text-slate-800 flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-blue-600 text-base">sort</span>
                    Item Ordering &amp; Sequence Options
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Choose how the serviced items are organized in the final PDF report.
                  </p>
                </div>
                {updatingOrder && (
                  <span className="text-[11px] text-blue-600 font-bold animate-pulse flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs animate-spin">sync</span>
                    Saving...
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  type="button"
                  disabled={updatingOrder}
                  onClick={() => handleArrangeChange("team")}
                  className={`flex flex-col items-center justify-center p-2.5 border rounded-xl transition text-xs font-medium ${arrangeBy === "team" ? "border-blue-600 bg-blue-50 text-blue-800 font-bold shadow-2xs" : "border-slate-200 hover:bg-white text-slate-700"} disabled:opacity-50`}
                >
                  <span className="material-symbols-outlined text-lg mb-0.5">groups</span>
                  <span>By Team</span>
                </button>

                <button
                  type="button"
                  disabled={updatingOrder}
                  onClick={() => handleArrangeChange("panel")}
                  className={`flex flex-col items-center justify-center p-2.5 border rounded-xl transition text-xs font-medium ${arrangeBy === "panel" ? "border-blue-600 bg-blue-50 text-blue-800 font-bold shadow-2xs" : "border-slate-200 hover:bg-white text-slate-700"} disabled:opacity-50`}
                >
                  <span className="material-symbols-outlined text-lg mb-0.5">grid_view</span>
                  <span>By Panel Area</span>
                </button>

                <button
                  type="button"
                  disabled={updatingOrder}
                  onClick={() => handleArrangeChange("team_panel")}
                  className={`flex flex-col items-center justify-center p-2.5 border rounded-xl transition text-xs font-medium ${arrangeBy === "team_panel" ? "border-blue-600 bg-blue-50 text-blue-800 font-bold shadow-2xs" : "border-slate-200 hover:bg-white text-slate-700"} disabled:opacity-50`}
                >
                  <span className="material-symbols-outlined text-lg mb-0.5">dashboard_customize</span>
                  <span>Team + Panel</span>
                </button>

                <button
                  type="button"
                  disabled={updatingOrder}
                  onClick={() => setArrangeBy("custom")}
                  className={`flex flex-col items-center justify-center p-2.5 border rounded-xl transition text-xs font-medium ${arrangeBy === "custom" ? "border-blue-600 bg-blue-50 text-blue-800 font-bold shadow-2xs" : "border-slate-200 hover:bg-white text-slate-700"} disabled:opacity-50`}
                >
                  <span className="material-symbols-outlined text-lg mb-0.5">drag_indicator</span>
                  <span>Custom</span>
                </button>
              </div>

              {(arrangeBy === "team" || arrangeBy === "team_panel") && (
                <div className="border border-slate-200 bg-white rounded-xl p-3 mt-2 shadow-2xs">
                  <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Team Sequence</h4>
                  <p className="text-[10px] text-slate-400 mb-2">Adjust priority order by moving teams up or down.</p>
                  <div className="space-y-1 max-w-md">
                    {customTeamsOrder.map((teamId, idx) => {
                      const matchedTeam = dynamicTeams.find(t => t.id === teamId || t.name === teamId);
                      const teamName = matchedTeam ? matchedTeam.name : (teamId === "team_1" ? "Team 1" : teamId === "team_2" ? "Team 2" : teamId === "team_3" ? "Team 3" : teamId);
                      return (
                        <div key={teamId} className="flex items-center justify-between bg-slate-50 border border-slate-200 px-2.5 py-1.5 rounded-lg">
                          <span className="text-xs font-bold text-slate-700">{teamName}</span>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              disabled={idx === 0 || updatingOrder}
                              onClick={() => moveTeamOrder(idx, "up")}
                              className="p-1 text-slate-600 hover:bg-slate-200 disabled:opacity-30 rounded transition"
                              title="Move Team Up"
                            >
                              <span className="material-symbols-outlined text-xs">arrow_upward</span>
                            </button>
                            <button
                              type="button"
                              disabled={idx === customTeamsOrder.length - 1 || updatingOrder}
                              onClick={() => moveTeamOrder(idx, "down")}
                              className="p-1 text-slate-600 hover:bg-slate-200 disabled:opacity-30 rounded transition"
                              title="Move Team Down"
                            >
                              <span className="material-symbols-outlined text-xs">arrow_downward</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {servicedProducts.length > 0 && (
            <>
              {/* Team Filtering Tabs */}
              {!isAbbWarranty && presentTeamIds.length > 1 && (
                <div className="border-b border-slate-200 pb-2.5">
                  <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
                    <button
                      type="button"
                      onClick={() => setSelectedTeamFilter("all")}
                      className={`px-3 py-1 text-xs font-bold rounded-full border transition-all duration-200 ${
                        selectedTeamFilter === "all"
                          ? "bg-slate-900 border-slate-900 text-white shadow-2xs"
                          : "bg-slate-100 border-slate-200 hover:bg-slate-200 text-slate-700"
                      }`}
                    >
                      All ({servicedProducts.length})
                    </button>
                    
                    {presentTeamIds.map(teamId => {
                      const count = servicedProducts.filter(p => getProductTeamId(p) === teamId).length;
                      return (
                        <button
                          key={teamId}
                          type="button"
                          onClick={() => setSelectedTeamFilter(teamId)}
                          className={`px-3 py-1 text-xs font-bold rounded-full border transition-all duration-200 ${
                            selectedTeamFilter === teamId
                              ? "bg-blue-600 border-blue-600 text-white shadow-2xs"
                              : "bg-slate-100 border-slate-200 hover:bg-slate-200 text-slate-700"
                          }`}
                        >
                          {getTeamDisplayName(teamId)} ({count})
                        </button>
                      );
                    })}

                    {hasUnassignedProducts && (
                      <button
                        type="button"
                        onClick={() => setSelectedTeamFilter("unassigned")}
                        className={`px-3 py-1 text-xs font-bold rounded-full border transition-all duration-200 ${
                          selectedTeamFilter === "unassigned"
                            ? "bg-blue-600 border-blue-600 text-white shadow-2xs"
                            : "bg-slate-100 border-slate-200 hover:bg-slate-200 text-slate-700"
                        }`}
                      >
                        Unassigned ({servicedProducts.filter(p => !getProductTeamId(p)).length})
                      </button>
                    )}
                  </div>
                </div>
              )}

              {filteredProducts.length > 0 ? (
                <div className="space-y-2.5">
                  {filteredProducts.map((product) => {
                    const originalIndex = servicedProducts.findIndex(p => p === product);
                    const isExpanded = expandedProductIndex === originalIndex;
                    const meta = getProductMeta(product);

                    return (
                    <div
                      key={product.id || product.serialNo || originalIndex}
                      className={`overflow-hidden rounded-xl border text-left transition-all duration-150 ${
                        isExpanded ? "border-blue-300 bg-blue-50/25" : "border-slate-200/80 bg-white hover:border-slate-300"
                      }`}
                    >
                      <div className="flex flex-col gap-2.5 px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span 
                              className={`h-2.5 w-2.5 rounded-full shrink-0 ${getProductDotColor(product)}`} 
                              title={
                                !((Array.isArray(product?.componentsToBeReplaced) && product.componentsToBeReplaced.some(c => c.name)) || (Array.isArray(product?.compReplace) && product.compReplace.some(Boolean)))
                                  ? "No components to be replaced"
                                  : ((Array.isArray(product?.componentsReplaced) && product.componentsReplaced.some(c => c.name)) || (Array.isArray(product?.newComp) && product.newComp.some(Boolean)))
                                    ? "Components to be replaced & replaced"
                                    : "Components to be replaced"
                              }
                            />
                            <span className="rounded-md bg-slate-900 px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wide text-white">
                              #{originalIndex + 1}
                            </span>
                            <p className="truncate text-xs sm:text-sm font-bold text-slate-900">
                              {getProductTitle(product, originalIndex)}
                            </p>
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {(meta.length ? meta : ["No summary details"]).map((item) => (
                              <span key={item} className="rounded-md bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-700 border border-slate-200">
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {canReviewReport && selectedTeamFilter === "all" && (
                            <div className="flex gap-1 mr-1">
                              <button
                                type="button"
                                disabled={originalIndex === 0 || updatingOrder}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveProduct(originalIndex, "up");
                                }}
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-1 text-slate-700 hover:bg-slate-50 disabled:opacity-30 transition"
                                title="Move Product Up"
                              >
                                <span className="material-symbols-outlined text-xs">arrow_upward</span>
                              </button>
                              <button
                                type="button"
                                disabled={originalIndex === servicedProducts.length - 1 || updatingOrder}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  moveProduct(originalIndex, "down");
                                }}
                                className="inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white p-1 text-slate-700 hover:bg-slate-50 disabled:opacity-30 transition"
                                title="Move Product Down"
                              >
                                <span className="material-symbols-outlined text-xs">arrow_downward</span>
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => setExpandedProductIndex((current) => current === originalIndex ? null : originalIndex)}
                            className={`shrink-0 rounded-lg px-3 py-1 text-xs font-bold transition ${
                              isExpanded
                                ? "bg-slate-200 text-slate-800 hover:bg-slate-300"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                          >
                            {isExpanded ? "Hide" : "Details"}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-blue-100 bg-white p-3.5 sm:p-4">
                          <SerialProductDetails
                            product={product}
                            visit={report}
                            complaint={{
                              complaintId: report.complaintRefId || report.complaintId || "",
                              status: report.finalStatus || "",
                              issue: report.complaint || ""
                            }}
                            showQr={Boolean(product.serialNo)}
                            showVisitDetails={false}
                          />
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-6 text-xs text-slate-500 bg-slate-50 rounded-xl border border-slate-200">
                  No products matched the selected team.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
      );
    })()
  );

  return isEngineerView ? <EngineerLayout>{content}</EngineerLayout> : content;
}


